/**
 * FileAttachButton — pick a file, upload it, hand back a `WorkAttachment`.
 *
 * Uploads via `uploadAttachment` (Firebase Storage when configured; a local
 * data-URL fallback in dev so the feature works end-to-end without faking a
 * link). Shared by the project detail pane and the task drawer so the upload
 * + error handling live in exactly one place.
 */

import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { uploadAttachment } from '../lib/storage';
import { C } from '../lib/tokens';
import type { WorkAttachment } from '../lib/db/types';

export function FileAttachButton({
  scope,
  id,
  addedBy,
  onAttach,
}: {
  scope: 'project' | 'task';
  id: string;
  addedBy: string;
  /** Persist the attachment (e.g. addProjectAttachment / addTaskAttachment). */
  onAttach: (att: Omit<WorkAttachment, 'id' | 'addedAt'>) => Promise<unknown> | void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const up = await uploadAttachment(scope, id, file);
      await onAttach({ kind: 'file', label: file.name, url: up.url, mime: up.mime, size: up.size, addedBy });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={onFile} />
      <button
        type="button"
        className="glass-btn"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        style={{ padding: '3px 10px', color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: busy ? 0.6 : 1 }}
      >
        <Upload size={12} /> {busy ? 'Uploading…' : 'Upload file'}
      </button>
      {err && <span style={{ fontSize: 11, color: C.orangeWarm }}>{err}</span>}
    </>
  );
}
